import React, {useState} from 'react';
import {Alert, Button, Card, Divider, Modal, Radio, Tag, Typography} from 'antd';
import {
    CalculatorOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    DollarOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import {usePositionForm} from "../../../providers/PositionFormProvider";
import {useProjectForm} from "../../../providers/ProjectFormProvider";

const {Title, Text, Paragraph} = Typography;

const PricingStrategyForm = ({onClose, ...props}) => {

    const {
        onFormSubmit: onProjectFormSubmit,
        project
    } = useProjectForm();
    const {
        formModel: position,
        onFormChange,
        onFormReset,
        onFormSubmit,
        isProjectUpdating,
        isLoading,
        isDisabled,
    } = usePositionForm();

    const [showAlert, setShowAlert] = useState(false);

    const selectedStrategy = position.fixedFeePricingStrategyValue();

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log("Form submitted, strategy:", selectedStrategy);

        let submitted = await onFormSubmit({});
        if (submitted && selectedStrategy && TB.find(project.positions, pos => !pos.hasFixedFeePricingStrategy())) {
            console.log("Showing alert");
            return setShowAlert(true);
        }
        return onClose();
    };

    const handleConfirm = async (applyToAll) => {
        // Here you would implement the logic to apply the strategy
        console.log(`Selected strategy: ${selectedStrategy}`);
        console.log(`Apply to all roles: ${applyToAll}`);
        setShowAlert(false);

        if (applyToAll) {
            let positions = TB.map(project.positions, pos => pos.setFixedFeePricingStrategy(selectedStrategy))

            let submitted = await onProjectFormSubmit({positions})

            if (submitted) {
                TB.toast("Pricing Strategy Applied to All Roles", {theme: 'success'});
                // Show confirmation message
                Modal.success({
                    title: 'Pricing Strategy Set',
                    content: (
                        <span>
                            Pricing strategy set to: <b>${position.getFixedFeePricingStrategyName(selectedStrategy)}</b>${applyToAll ? ' for all project roles' : ''}
                        </span>
                    ),
                });
            }
        }

        return onClose()
    };


    const OptionCard = ({value, description, children}) => (
        <div className="w-1/3 flex flex-col items-stretch">
            <Card
                className={TB.mapClass("clickable mb-4 transition-shadow", {
                    "border-purple-400 shadow-[0_0_0_2px_rgba(145,62,240,0.3)]": value === selectedStrategy,
                    "hover:shadow-md": value !== selectedStrategy
                })}
                onClick={() => {
                    onFormChange({...(position.setFixedFeePricingStrategy(value))})
                }}
                bodyStyle={{padding: 16}}
            >
                <div className="flex items-start">
                    <Radio value={value} className="mt-1 mr-3"/>
                    <div>
                        <Title level={5} style={{marginTop: 0, marginBottom: 8}}>{position.getFixedFeePricingStrategyName(value)}</Title>
                        <div>
                            <Paragraph className="text-sm" style={{marginBottom: 12}}>{description}</Paragraph>
                            <span className="pl-3">
                                {children}
                            </span>
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );

    const RequiredLabel = () => (
        <Tag color="error" className="text-xs mb-1.5">Required</Tag>
    );

    const OptionalLabel = () => (
        <Text type="secondary" className="text-xs mb-1.5">Optional:</Text>
    );

    const CalculationBox = ({calculations}) => (
        <div style={{background: '#FFF9E6', border: '1px solid #FFE58F', borderRadius: 4, padding: 12, marginTop: 16}}>
            <div style={{display: 'flex', alignItems: 'center', marginBottom: 8}}>
                <CalculatorOutlined style={{color: '#FAAD14', marginRight: 8}}/>
                <Text strong style={{fontSize: 11, color: '#D46B08'}}>CALCULATION</Text>
            </div>
            {calculations.map((calc, index) => (
                <div key={index} style={{display: 'flex', alignItems: 'center', marginBottom: 4}}>
                    <CheckCircleOutlined style={{color: '#FAAD14', marginRight: 8, fontSize: 11}}/>
                    <Text style={{fontSize: 11}}>{calc}</Text>
                </div>
            ))}
        </div>
    );

    return (
        <div className="m-auto">
            <div className="flex flex-col items-stretch">

                <Text className="mb-3">
                    Please select an option below. Each candidate's proposal will follow the same selected pricing strategy
                </Text>

                <Radio.Group
                    value={selectedStrategy}
                    onChange={e => {
                        console.log('Selected strategy: ', e.target.value, {position: {...(position.setFixedFeePricingStrategy(e.target.value))}})
                        onFormChange({...(position.setFixedFeePricingStrategy(e.target.value))})
                    }}
                    style={{width: '100%'}}
                >
                    <div className="flex flex-row items-start justify-evenly mx-[-8px] gap-2" style={{width: '100%'}}>
                        <OptionCard
                            value="total-payout"
                            description="Talent will provide a total price for the entire project (not broken down by deliverable)"
                        >
                            <div className="flex flex-col gap-3 mb-2 text-xs">

                                <div className="flex flex-col items-start">
                                    <RequiredLabel/>
                                    <p className="flex flex-row gap-0.5 my-1 pl-3 text-xs items-center">
                                        <span className="h-4 pt-[1px] w-4 flex flex-col items-center justify-start">
                                            <DollarOutlined/>
                                        </span>
                                        <span>Total proposal payout</span>
                                    </p>
                                </div>
                                <div className="flex flex-col items-start">
                                    <OptionalLabel/>
                                    <p className="flex flex-row gap-0.5 my-1 pl-3 text-xs items-center">
                                        <span className="h-4 pt-[1px] w-4 flex flex-col items-center justify-start">
                                            <CalendarOutlined/>
                                        </span>
                                        <span>Target delivery date for each deliverable</span>
                                    </p>
                                </div>
                            </div>
                        </OptionCard>

                        <OptionCard
                            value="per-deliverable"
                            description="Talent will provide a price per deliverable which will be added together to calculate their total proposed payout"
                        >
                            <div className="flex flex-col gap-3 mb-2 text-xs">


                                <div className="flex flex-col items-start">
                                    <RequiredLabel/>
                                    <p className="flex flex-row gap-0.5 my-1 pl-3 text-xs items-center">
                                        <span className="h-4 pt-[1px] w-4 flex flex-col items-center justify-start">
                                            <DollarOutlined/>
                                        </span>
                                        <span>Payout amount for each deliverable</span>
                                    </p>
                                </div>
                                <div className="flex flex-col items-start">
                                    <OptionalLabel/>
                                    <p className="flex flex-row gap-0.5 my-1 pl-3 text-xs items-center">
                                        <span className="h-4 pt-[1px] w-4 flex flex-col items-center justify-start">
                                            <CalendarOutlined/>
                                        </span>
                                        <span>Target delivery date for each deliverable</span>
                                    </p>
                                    <p className="flex flex-row gap-0.5 my-1 pl-3 text-xs items-center">
                                        <span className="h-4 pt-[1px] w-4 flex flex-col items-center justify-start">
                                            <ClockCircleOutlined/>
                                        </span>
                                        <span>Estimated hours for each deliverable</span>
                                    </p>
                                </div>
                            </div>
                            <CalculationBox calculations={[
                                "Total Proposal Payout = Sum of all Deliverable Payouts"
                            ]}/>
                        </OptionCard>

                        <OptionCard
                            value="hourly-rate"
                            description="Talent will provide their bill rate and estimated hours per deliverable, which will be used to calculate their proposed payout."
                        >
                            <div className="flex flex-col gap-3 mb-2 text-xs">

                                <div className="flex flex-col items-start">
                                    <RequiredLabel/>
                                    <p className="flex flex-row gap-0.5 my-1 pl-3 text-xs items-center">
                                        <span className="h-4 pt-[1px] w-4 flex flex-col items-center justify-start">
                                            <DollarOutlined/>
                                        </span>
                                        <span>Hourly rate</span>
                                    </p>
                                    <p className="flex flex-row gap-0.5 my-1 pl-3 text-xs items-center">
                                        <span className="h-4 pt-[1px] w-4 flex flex-col items-center justify-start">
                                            <ClockCircleOutlined/>
                                        </span>
                                        <span>Estimated hours for each deliverable</span>
                                    </p>
                                </div>
                                <div className="flex flex-col items-start">
                                    <OptionalLabel/>
                                    <p className="flex flex-row gap-0.5 my-1 pl-3 text-xs items-center">
                                        <span className="h-4 pt-[1px] w-4 flex flex-col items-center justify-start">
                                            <CalendarOutlined/>
                                        </span>
                                        <span>Target delivery date for each deliverable</span>
                                    </p>
                                </div>

                            </div>
                            <CalculationBox calculations={[
                                "Deliverable Payout = Hourly Rate × Estimated Hours",
                                "Total Proposal Payout = Sum of all Deliverable Payouts"
                            ]}/>
                        </OptionCard>
                    </div>
                </Radio.Group>

                <div className="flex flex-row justify-end gap-3 mt-4">
                    <Button
                        htmlType="button"
                        type="default"
                        onClick={async () => {
                            await onFormReset();
                            return onClose()
                        }}
                        className="inline-flex"
                    >
                        Cancel
                    </Button>
                    <Button
                        htmlType="button"
                        type="primary"
                        onClick={handleSubmit}
                        disabled={!selectedStrategy}
                        className="inline-flex"
                    >
                        Set Pricing Strategy
                    </Button>
                </div>

                <Divider className="my-3"/>
            </div>

            <Modal
                title={
                    <div className="flex items-center">
                        <InfoCircleOutlined style={{marginRight: 8}}/>
                        Apply to All Project Roles?
                    </div>
                }
                open={showAlert}
                onClose={() => setShowAlert(false)}
                footer={(
                    <div className="flex flex-row justify-end gap-3">
                        <Button key="cancel" onClick={() => handleConfirm(false)}>
                            No, just this role
                        </Button>
                        <Button key="submit" type="primary" onClick={() => handleConfirm(true)}>
                            Yes, apply to all roles
                        </Button>
                    </div>
                )}
            >

                <p className="text-sm">
                    Would you like to apply the <Text strong>{position.getFixedFeePricingStrategyName(selectedStrategy)}</Text> pricing
                    strategy to all of the project roles?
                </p>
                <Alert
                    className="mx-auto max-w-96 mt-4 mb-3"
                    message={
                        <div className="flex flex-col items-start gap-1">
                            <Text className="text-xs font-medium">
                                Note: Each candidate's proposal will follow the same selected pricing strategy
                            </Text>
                            <Text className="text-xxs leading-normal inline">Currently at this time, the pricing
                                strategy cannot be changed once the project scoping is complete and open to talent
                                proposals.</Text>
                        </div>
                    }
                    type="warning"
                    showIcon
                />
            </Modal>
        </div>
    );
};

export default PricingStrategyForm;